/**
 * API Client for making requests to the backend API
 */

import { apiEndpoint } from '@/config';

const API_BASE_URL = apiEndpoint;

export interface ApiResponse<T = any> {
	success: boolean;
	data?: T;
	message?: string;
	meta?: {
		total?: number;
		limit?: number;
		offset?: number;
	};
	error?: string;
}

export interface ListParams {
	limit?: number;
	offset?: number;
	[key: string]: any;
}

class ApiClient {
	private baseUrl: string;

	constructor(baseUrl: string = API_BASE_URL) {
		this.baseUrl = baseUrl;
	}

	private getCookie(name: string): string | null {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			const cookieValue = parts.pop()?.split(';').shift();
			return cookieValue || null;
		}
		return null;
	}

	private async request<T>(
		endpoint: string,
		options: RequestInit = {}
	): Promise<ApiResponse<T>> {
		const url = `${this.baseUrl}${endpoint}`;

		// Get token from jwt cookie
		const token = this.getCookie('jwt');

		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
		};

		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		// Merge with provided headers
		if (options.headers) {
			Object.assign(headers, options.headers);
		}

		try {
			const response = await fetch(url, {
				...options,
				headers,
				credentials: 'include', // Send cookies with request
			});

			const data = await response.json();

			if (!response.ok) {
				return {
					success: false,
					error: data.message || data.error || 'Request failed',
					message: data.message || data.error,
				};
			}

			return data;
		} catch (error) {
			console.error('API request failed:', error);
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Network error',
			};
		}
	}

	async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
		const queryString = params
			? '?' +
				Object.entries(params)
					.filter(([_, value]) => value !== undefined && value !== null)
					.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
					.join('&')
			: '';

		return this.request<T>(`${endpoint}${queryString}`, {
			method: 'GET',
		});
	}

	async post<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async put<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			method: 'PUT',
			body: JSON.stringify(body),
		});
	}

	async patch<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			method: 'PATCH',
			body: JSON.stringify(body),
		});
	}

	async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			method: 'DELETE',
		});
	}

	// CRUD operations
	async list<T>(resource: string, params?: ListParams): Promise<ApiResponse<T[]>> {
		return this.get<T[]>(`/${resource}`, params);
	}

	async getById<T>(resource: string, id: string): Promise<ApiResponse<T>> {
		return this.get<T>(`/${resource}/${id}`);
	}

	async create<T>(resource: string, data: Partial<T>): Promise<ApiResponse<T>> {
		return this.post<T>(`/${resource}`, data);
	}

	async update<T>(resource: string, id: string, data: Partial<T>): Promise<ApiResponse<T>> {
		return this.put<T>(`/${resource}/${id}`, data);
	}

	async remove<T>(resource: string, id: string): Promise<ApiResponse<T>> {
		return this.delete<T>(`/${resource}/${id}`);
	}
}

export const apiClient = new ApiClient();
export default apiClient;
